import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  login, setToken, setStoredUser, autoInitBodacc,
  onboardingPassword, mfaSetup, mfaActivate, mfaVerify, mfaHelp,
  getJuryLegalDocument, acceptJuryLegal,
} from '../api';
import { useAuth } from '../App';

const REDIRECTS = {
  startup: '/startup/dashboard',
  investisseur: '/search',
  analyste: '/analyst/dossiers',
  admin: '/admin/dashboard',
  jury: '/jury',
};

const inputStyle = {
  width: '100%', padding: '12px 16px', border: '1px solid var(--border)',
  borderRadius: 8, fontSize: 14, fontFamily: 'inherit',
};
const labelStyle = {
  display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: 'var(--text-light)',
};

// Regles verifiees aussi cote serveur ; ici pour donner un retour immediat.
const PASSWORD_RULES = [
  { label: '12 caractères minimum', ok: (p) => p.length >= 12 },
  { label: 'une minuscule', ok: (p) => /[a-z]/.test(p) },
  { label: 'une majuscule', ok: (p) => /[A-Z]/.test(p) },
  { label: 'un chiffre', ok: (p) => /[0-9]/.test(p) },
  { label: 'un caractère spécial', ok: (p) => /[^a-zA-Z0-9]/.test(p) },
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  // Etape courante du parcours renforce : null = formulaire de connexion.
  const [step, setStep] = useState(null);          // change_password | enroll_mfa | verify_mfa
  const [challenge, setChallenge] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [enroll, setEnroll] = useState(null);      // { qr_svg, secret, recovery_codes, account }
  const [codesAcknowledged, setCodesAcknowledged] = useState(false);
  const [code, setCode] = useState('');
  const [helpSent, setHelpSent] = useState(false);

  // Engagement de confidentialité (dernière étape, membres du jury).
  const [legalDoc, setLegalDoc] = useState(null);
  const [legalChecked, setLegalChecked] = useState(false);
  // La case ne s'active qu'une fois le texte parcouru jusqu'au bout : on ne
  // prouve pas la lecture, mais on évite l'accord donné sans avoir fait défiler.
  const [legalRead, setLegalRead] = useState(false);
  const legalBoxRef = useRef(null);

  useEffect(() => {
    if (step !== 'accept_legal') return;
    getJuryLegalDocument()
      .then(doc => {
        setLegalDoc(doc);
        // Texte entièrement visible sans défilement (grand écran) : rien à
        // parcourir, la case est immédiatement disponible.
        requestAnimationFrame(() => {
          const el = legalBoxRef.current;
          if (el && el.scrollHeight <= el.clientHeight + 4) setLegalRead(true);
        });
      })
      .catch(err => setError(err.message));
  }, [step]);

  const onLegalScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 12) setLegalRead(true);
  };

  const handleAcceptLegal = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { applyResponse(await acceptJuryLegal(challenge, true)); }
    catch (err) { setError(err.message); }
    setLoading(false);
  };

  // Une reponse peut soit ouvrir la session, soit demander une etape de plus.
  const applyResponse = (res) => {
    if (res.token) {
      setToken(res.token);
      setStoredUser(res.user);
      setUser(res.user);
      autoInitBodacc().catch(() => {});
      navigate(REDIRECTS[res.user.role] || '/');
      return true;
    }
    setChallenge(res.challenge_token);
    setStep(res.action);
    setError('');
    setInfo('');
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      applyResponse(await login({ email, password }));
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError('Les deux mots de passe ne correspondent pas.'); return; }
    setError(''); setLoading(true);
    try {
      applyResponse(await onboardingPassword(challenge, newPassword));
      setNewPassword(''); setConfirmPassword('');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const startEnroll = async () => {
    setError(''); setLoading(true);
    try { setEnroll(await mfaSetup(challenge)); }
    catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try { applyResponse(await mfaActivate(challenge, code)); setCode(''); }
    catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await mfaVerify(challenge, code);
      if (res.recovery_code_used) {
        // L'utilisateur doit savoir qu'il vient de consommer un code papier.
        window.alert(
          `Vous venez d'utiliser un code de secours. Il ne vous en reste que `
          + `${res.recovery_codes_remaining}. Prévenez l'administrateur si vous `
          + `n'avez plus accès à votre application d'authentification.`
        );
      }
      applyResponse(res);
      setCode('');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const handleHelp = async () => {
    setLoading(true);
    try {
      const res = await mfaHelp(challenge, email, '');
      setHelpSent(true); setInfo(res.message); setError('');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const backToLogin = () => {
    setStep(null); setChallenge(''); setEnroll(null); setCode('');
    setNewPassword(''); setConfirmPassword('');
    setCodesAcknowledged(false); setHelpSent(false);
    setLegalDoc(null); setLegalChecked(false); setLegalRead(false);
    setError(''); setInfo('');
  };

  const passwordOk = PASSWORD_RULES.every(r => r.ok(newPassword));

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {/* Marges resserrees sur petit ecran : 24 + 40 px de chaque cote ne
          laissaient que ~230 px de contenu utile sur un telephone. */}
      <div style={{ width: '100%',
        maxWidth: step === 'accept_legal' ? 660 : step === 'enroll_mfa' ? 560 : 440,
        padding: 'clamp(12px, 4vw, 24px)' }}>
        <div className="card" style={{ padding: 'clamp(20px, 5vw, 40px)' }}>

          {error && (
            <div style={{ background: '#FFEBEE', color: '#C62828', padding: 12, borderRadius: 8,
              marginBottom: 16, fontSize: 14 }}>{error}</div>
          )}
          {info && (
            <div style={{ background: '#E8F5E9', color: '#2E7D32', padding: 12, borderRadius: 8,
              marginBottom: 16, fontSize: 14 }}>{info}</div>
          )}

          {/* ================= ETAPE 0 : identifiants ================= */}
          {step === null && (
            <>
              <h1 style={{ fontSize: 24, marginBottom: 8, textAlign: 'center' }}>Connexion</h1>
              <p style={{ textAlign: 'center', color: 'var(--text-light)', marginBottom: 32, fontSize: 14 }}>
                Plateforme ISI - L'Officiel des Fintech
              </p>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                    style={inputStyle} placeholder="votre@email.fr" />
                </div>
                <div style={{ marginBottom: 8 }}>
                  <label style={labelStyle}>Mot de passe</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                    style={inputStyle} placeholder="Votre mot de passe" />
                </div>
                <div style={{ textAlign: 'right', marginBottom: 24 }}>
                  <Link to="/forgot-password" style={{ fontSize: 12.5, color: 'var(--text-light)', textDecoration: 'none' }}>
                    Mot de passe oublié ?
                  </Link>
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading}
                  style={{ width: '100%', justifyContent: 'center', padding: '14px 0', fontSize: 15 }}>
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>
              </form>
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <Link to="/register" style={{ fontSize: 14 }}>Pas de compte ? S'inscrire</Link>
              </div>
            </>
          )}

          {/* ============ ETAPE 1 : changement de mot de passe imposé ============ */}
          {step === 'change_password' && (
            <>
              <h1 style={{ fontSize: 22, marginBottom: 8 }}>Choisissez votre mot de passe</h1>
              <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
                Le mot de passe qui vous a été transmis est provisoire. Vous devez en définir
                un nouveau avant d'accéder à la plateforme.
              </p>
              <form onSubmit={handleNewPassword}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>Nouveau mot de passe</label>
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                    required autoFocus style={inputStyle} />
                </div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px', fontSize: 12.5 }}>
                  {PASSWORD_RULES.map(r => {
                    const ok = r.ok(newPassword);
                    return (
                      <li key={r.label} style={{ color: ok ? '#2E7D32' : 'var(--text-light)', marginBottom: 2 }}>
                        {ok ? '✓' : '○'} {r.label}
                      </li>
                    );
                  })}
                </ul>
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Confirmation</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                    required style={inputStyle} />
                </div>
                <button type="submit" className="btn btn-primary" disabled={loading || !passwordOk}
                  style={{ width: '100%', justifyContent: 'center', padding: '14px 0', fontSize: 15 }}>
                  {loading ? 'Enregistrement...' : 'Valider et continuer'}
                </button>
              </form>
            </>
          )}

          {/* ============ ETAPE 2 : enrôlement de l'application ============ */}
          {step === 'enroll_mfa' && (
            <>
              <h1 style={{ fontSize: 22, marginBottom: 8 }}>Sécurisez votre compte</h1>
              <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
                Votre accès est protégé par une double authentification. Enregistrez votre compte
                dans <strong>Google Authenticator</strong> (ou toute application compatible :
                Microsoft Authenticator, Authy, 1Password…).
              </p>

              {!enroll ? (
                <button onClick={startEnroll} className="btn btn-primary" disabled={loading}
                  style={{ width: '100%', justifyContent: 'center', padding: '14px 0', fontSize: 15 }}>
                  {loading ? 'Préparation...' : 'Afficher mon QR code'}
                </button>
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 16 }}>
                    <img src={enroll.qr_svg} alt="QR code d'enrôlement" width={220} height={220}
                      style={{ border: '1px solid var(--border)', borderRadius: 12, background: 'white',
                        maxWidth: '100%', height: 'auto' }} />
                    {/* Un telephone ne peut pas photographier le QR code qu'il
                        affiche lui-meme : ce lien ouvre directement l'application
                        d'authentification installee sur l'appareil. Sans effet
                        sur un ordinateur, ou le scan reste la voie normale. */}
                    <div style={{ marginTop: 12 }}>
                      <a href={enroll.otpauth_uri}
                        className="btn btn-outline"
                        style={{ fontSize: 13, textDecoration: 'none' }}>
                        Ouvrir dans mon application d'authentification
                      </a>
                      <div style={{ fontSize: 11.5, color: 'var(--text-light)', marginTop: 6 }}>
                        Depuis un téléphone : si vous consultez cette page sur l'appareil qui
                        porte l'application, utilisez ce bouton plutôt que le QR code.
                      </div>
                    </div>
                  </div>
                  <details style={{ fontSize: 12.5, color: 'var(--text-light)', marginBottom: 20 }}>
                    <summary style={{ cursor: 'pointer' }}>Impossible de scanner le QR code ?</summary>
                    <p style={{ marginBottom: 4 }}>Saisissez cette clé manuellement dans votre application :</p>
                    <code style={{ display: 'block', padding: '8px 10px', background: 'var(--bg-secondary)',
                      borderRadius: 6, wordBreak: 'break-all', fontSize: 13, letterSpacing: 1 }}>
                      {enroll.secret}
                    </code>
                    <p style={{ marginTop: 6, marginBottom: 0 }}>Compte : {enroll.account}</p>
                  </details>

                  <div style={{ padding: 16, background: '#FFF8E1', border: '1px solid #FFE082',
                    borderRadius: 10, marginBottom: 20 }}>
                    <strong style={{ fontSize: 14, color: '#8D6E20' }}>Vos codes de secours</strong>
                    <p style={{ fontSize: 12.5, color: '#8D6E20', margin: '6px 0 10px', lineHeight: 1.5 }}>
                      Conservez-les hors de votre téléphone. Chacun ne fonctionne qu'une fois et
                      permet de vous connecter si vous perdez votre application.
                      <strong> Ils ne seront plus jamais affichés.</strong>
                    </p>
                    <div className="keep-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6,
                      fontFamily: 'monospace', fontSize: 14 }}>
                      {enroll.recovery_codes.map(c => <span key={c}>{c}</span>)}
                    </div>
                    <button type="button"
                      onClick={() => navigator.clipboard?.writeText(enroll.recovery_codes.join('\n'))}
                      style={{ marginTop: 10, padding: '4px 12px', border: '1px solid #FFCC80',
                        borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: 12,
                        fontFamily: 'inherit', color: '#8D6E20' }}>
                      Copier les codes
                    </button>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
                      fontSize: 12.5, color: '#8D6E20', cursor: 'pointer' }}>
                      <input type="checkbox" checked={codesAcknowledged}
                        onChange={e => setCodesAcknowledged(e.target.checked)} />
                      J'ai mis mes codes de secours en lieu sûr
                    </label>
                  </div>

                  <form onSubmit={handleActivate}>
                    <label style={labelStyle}>Code à 6 chiffres affiché par votre application</label>
                    <input type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                      value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))} required
                      style={{ ...inputStyle, letterSpacing: 8, textAlign: 'center', fontSize: 20 }}
                      placeholder="000000" />
                    <button type="submit" className="btn btn-primary"
                      disabled={loading || !codesAcknowledged || code.length !== 6}
                      style={{ width: '100%', justifyContent: 'center', padding: '14px 0',
                        fontSize: 15, marginTop: 16 }}>
                      {loading ? 'Vérification...' : 'Activer et me connecter'}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          {/* ============ ETAPE 3 : saisie du code à chaque connexion ============ */}
          {step === 'verify_mfa' && (
            <>
              <h1 style={{ fontSize: 22, marginBottom: 8 }}>Vérification en deux étapes</h1>
              <p style={{ color: 'var(--text-light)', marginBottom: 24, fontSize: 14, lineHeight: 1.6 }}>
                Saisissez le code à 6 chiffres affiché par votre application d'authentification,
                ou l'un de vos codes de secours.
              </p>
              <form onSubmit={handleVerify}>
                <input type="text" autoComplete="one-time-code" value={code} autoFocus
                  onChange={e => setCode(e.target.value)} required
                  style={{ ...inputStyle, letterSpacing: 4, textAlign: 'center', fontSize: 18 }}
                  placeholder="000000" />
                <button type="submit" className="btn btn-primary" disabled={loading || !code.trim()}
                  style={{ width: '100%', justifyContent: 'center', padding: '14px 0',
                    fontSize: 15, marginTop: 16 }}>
                  {loading ? 'Vérification...' : 'Se connecter'}
                </button>
              </form>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                {helpSent ? (
                  <p style={{ fontSize: 13, color: '#2E7D32', margin: 0, lineHeight: 1.6 }}>
                    L'administrateur a été prévenu. Il réinitialisera votre double authentification
                    et vous pourrez alors enregistrer à nouveau votre application.
                  </p>
                ) : (
                  <>
                    <p style={{ fontSize: 12.5, color: 'var(--text-light)', marginBottom: 10, lineHeight: 1.6 }}>
                      Téléphone perdu, application réinstallée, codes de secours égarés ?
                    </p>
                    <button type="button" onClick={handleHelp} disabled={loading}
                      style={{ width: '100%', padding: '10px 0', border: '1px solid var(--border)',
                        borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13.5,
                        fontFamily: 'inherit', color: 'var(--primary)', fontWeight: 600 }}>
                      Prévenir l'administrateur pour débloquer mon accès
                    </button>
                  </>
                )}
              </div>
            </>
          )}

          {/* ====== ETAPE 4 : engagement de confidentialité (membres du jury) ====== */}
          {step === 'accept_legal' && (
            <>
              <h1 style={{ fontSize: 20, marginBottom: 8 }}>
                {legalDoc ? legalDoc.title : 'Engagement de confidentialité'}
              </h1>
              <p style={{ color: 'var(--text-light)', marginBottom: 16, fontSize: 13.5, lineHeight: 1.6 }}>
                Dernière étape avant l'accès à la plateforme. Prenez le temps de lire ce texte :
                votre accord est enregistré et conservé.
              </p>

              {!legalDoc ? (
                <p style={{ fontSize: 14, color: 'var(--text-light)' }}>Chargement du texte…</p>
              ) : (
                <form onSubmit={handleAcceptLegal}>
                  <div ref={legalBoxRef} onScroll={onLegalScroll}
                    style={{ maxHeight: '45vh', overflowY: 'auto', padding: '16px 18px',
                      border: '1px solid var(--border)', borderRadius: 10,
                      background: 'var(--bg-secondary)', fontSize: 13.5, lineHeight: 1.65 }}>
                    <p style={{ marginBottom: 12 }}>{legalDoc.intro}</p>
                    <p style={{ marginBottom: 8, fontWeight: 600 }}>{legalDoc.lead}</p>
                    <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
                      {legalDoc.items.map((it, i) => (
                        <li key={i} style={{ marginBottom: 6 }}>{it}</li>
                      ))}
                    </ul>
                    <p style={{ fontSize: 12.5, color: 'var(--text-light)' }}>
                      Vos données personnelles sont traitées par Investance Partners conformément
                      au RGPD. Pour exercer vos droits :{' '}
                      <a href={`mailto:${legalDoc.rgpd_email}`}>{legalDoc.rgpd_email}</a>
                    </p>
                  </div>

                  {!legalRead && (
                    <p style={{ fontSize: 12, color: '#8D6E20', margin: '8px 0 0' }}>
                      Faites défiler le texte jusqu'au bout pour pouvoir cocher la case.
                    </p>
                  )}

                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16,
                    padding: '12px 14px', borderRadius: 10, fontSize: 13.5, lineHeight: 1.55,
                    border: `1.5px solid ${legalChecked ? '#27AE60' : 'var(--border)'}`,
                    background: legalChecked ? 'rgba(39,174,96,0.06)' : 'transparent',
                    cursor: legalRead ? 'pointer' : 'not-allowed',
                    opacity: legalRead ? 1 : 0.6 }}>
                    <input type="checkbox" checked={legalChecked} disabled={!legalRead}
                      onChange={e => setLegalChecked(e.target.checked)}
                      style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }} />
                    <span>{legalDoc.checkbox}</span>
                  </label>

                  <button type="submit" className="btn btn-primary" disabled={loading || !legalChecked}
                    style={{ width: '100%', justifyContent: 'center', padding: '14px 0',
                      fontSize: 15, marginTop: 16 }}>
                    {loading ? 'Enregistrement...' : 'J\'accepte et j\'accède à la plateforme'}
                  </button>
                  <p style={{ fontSize: 11.5, color: 'var(--text-light)', marginTop: 10, textAlign: 'center' }}>
                    Version {legalDoc.version} — votre accord, sa date et la référence exacte de ce
                    texte sont conservés.
                  </p>
                </form>
              )}
            </>
          )}

          {step !== null && (
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <button type="button" onClick={backToLogin}
                style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
                  color: 'var(--text-light)', fontFamily: 'inherit', textDecoration: 'underline' }}>
                Revenir à l'écran de connexion
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
